import { Either, Fail, Success } from 'monet';

/**
 * @description Шлюз рабочего графика.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function WorkScheduleGateway(axios) {
  /**
   * @description Очистка графика работы сотрудника.
   * @param {number} employeeId
   * @param {string} yearAndMonth
   * @returns {Promise<Either>}
   */
  this.clearEmployeeWorkSchedule = async (employeeId, yearAndMonth) => {
    try {
      const response = await axios
        .delete(`/api/work-schedule/${employeeId}/clear`, { params: { year_and_month: yearAndMonth } });
      return Either.right(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Either.left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить месячное расписание.
   * @param {string} yearAndMonth Год и месяц.
   * @returns {Promise<any>}
   */
  this.getByMonth = async (yearAndMonth = '') => {
    const params = {};
    params.year_and_month = yearAndMonth ?? null;
    try {
      const response = await axios.get('/api/monthly-work-schedule', { params });
      return Either.right(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 403) {
        return Either.left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить расписание администраторов для печати.
   * @param {string} yearAndMonth Год и месяц.
   * @returns {Promise<any>}
   */
  this.viewWorkScheduleAdministratorForPrint = async (yearAndMonth) => {
    const params = yearAndMonth ? { year_and_month: yearAndMonth } : {};
    return axios
      .get('/api/work-schedule/administrator/print', { params })
      .then(
        async (response) => Either.right(response.data),
        async (error) => {
          if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data);
          }
          throw error;
        },
      );
  };

  /**
   * @description Получить расписание асситсента для печати.
   * @param {string} yearAndMonth Год и месяц.
   * @returns {Promise<any>}
   */
  this.viewWorkScheduleAssistantForPrint = async (yearAndMonth) => {
    const params = yearAndMonth ? { year_and_month: yearAndMonth } : {};
    return axios
      .get('/api/work-schedule/assistant/print', { params })
      .then(
        async (response) => Either.right(response.data),
        async (error) => {
          if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data);
          }
          throw error;
        },
      );
  };

  /**
   * @description Получить расписание доктора для печати.
   * @param {string} yearAndMonth Год и месяц.
   * @returns {Promise<any>}
   */
  this.viewWorkScheduleDoctorForPrint = async (yearAndMonth) => {
    const params = yearAndMonth ? { year_and_month: yearAndMonth } : {};
    return axios
      .get('/api/work-schedule/doctor/print', { params })
      .then(
        async (response) => Either.right(response.data),
        async (error) => {
          if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
            return Either.left(error.response.data);
          }
          throw error;
        },
      );
  };

  /**
   * @description Установить базовое рабочее время.
   * @param {string} endsTime   Время окончания.
   * @param {string} startsTime Время начала.
   * @returns {Promise<Either>}
   */
  this.setBasicWorkTime = async (endsTime, startsTime) => {
    try {
      const params = { ends_time: endsTime, starts_time: startsTime };
      const response = await axios.put('/api/work-time', params);
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить выходной день для кабинета.
   * @param {number} cabinetId Идентификатор кабинета.
   * @param {string} date      Дата.
   * @returns {Promise<Either>}
   */
  this.setCabinetDayOff = async (cabinetId, date) => {
    try {
      const response = await axios.put(
        `/api/cabinets/${cabinetId}/schedule-days/${date}/off`,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить выходной день.
   * @param {string} date Дата.
   * @returns {Promise<Either>}
   */
  this.setDayOff = async (date) => {
    try {
      const response = await axios.put(`/api/schedule-days/${date}/off`);
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить базовое время работы сотрудника.
   * @param {numeric} employeeId Идентификатор сотрудника.
   * @param {string}  endsTime   Время окончания.
   * @param {string}  startsTime Время начала.
   * @returns {Promise<Either>}
   */
  this.setEmployeeBasicWorkTime = async (employeeId, endsTime, startsTime) => {
    try {
      const params = { ends_time: endsTime, starts_time: startsTime };
      const response = await axios.put(
        `/api/employees/${employeeId}/work-time`,
        params,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  this.changeEmployeeWorkTimeHours = async (employeeId, endsTime, startsTime, date) => {
    try {
      const params = { ends_time: endsTime, starts_time: startsTime };
      const response = await axios.put(
        `/api/employees/${employeeId}/schedule-days/${date}/hours`,
        params,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить выходной день сотрудника.
   * @param {string} employeeId Идентификатор сотрудника.
   * @param {string} date       Дата.
   * @returns {Promise<Either>}
   */
  this.setEmployeeDayOff = async (employeeId, date) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/schedule-days/${date}/off`,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить рабочий день сотрудника.
   * @param {any[]}  cabinets   Рабочие часы по кабинетам.
   * @param {string} employeeId Идентификатор сотрудника.
   * @param {string} date       Дата.
   * @returns {Promise<Either>}
   */
  this.setEmployeeWorkDay = async (
    cabinets,
    employeeId,
    date,
  ) => {
    const params = {
      cabinets: cabinets.map((cabinet) => ({
        id: cabinet.cabinetId,
        work_times: cabinet.workTimes.map((workTime) => ({
          ends_time: workTime.to,
          starts_time: workTime.from,
          assistant_id: workTime.assistantId,
        })),
      })),
    };
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/schedule-days/${date}/work`,
        params,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить режим работы сотрудника по клинике.
   * @param {string}  employeeId   Идентификатор сотрудника.
   * @param {string}  yearAndMonth Год и месяц.
   * @returns {Promise<Either>}
   */
  this.setEmployeeWorkMonthByClinic = async (employeeId, yearAndMonth) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/work-mode/by-clinic`,
        {
          year_and_month: yearAndMonth,
        },
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить сотруднику свободный режим работы.
   * @param {string}  employeeId   Идентификатор сотрудника.
   * @param {string}  yearAndMonth Год и месяц.
   * @returns {Promise<Either>}
   */
  this.setEmployeeWorkModeByFree = async (employeeId, yearAndMonth) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/work-mode/free`,
        {
          year_and_month: yearAndMonth,
        },
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить рабочий месяц сотрудника по дням недели.
   * @param {string} employeeId Идентификатор сотрудника.
   * @param {string} startsDate Дата начала отсчета.
   * @param {any[]}  weekdays   Дни недели.
   * @returns {Promise<Either>}
   */
  this.setEmployeeWorkMonthByWeekdays = async (
    employeeId,
    startsDate,
    weekdays,
  ) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/work-mode/by-weekdays`,
        {
          starts_date: startsDate,
          weekdays,
        },
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить рабочий месяц сотрудника через дни.
   * @param {string}  employeeId    Идентификатор сотрудника.
   * @param {number}  numberOffDays Количество выходных дней.
   * @param {string}  startsDate    Дата начала отсчета.
   * @param {any[]}   workDays      Рабочие дни.
   * @returns {Promise<Either>}
   */
  this.setEmployeeWorkMonthThroughDays = async (
    employeeId,
    numberOffDays,
    startsDate,
    workDays,
  ) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/work-mode/by-through-days`,
        {
          number_off_days: numberOffDays,
          starts_date: startsDate,
          work_days: workDays,
        },
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить ассистента сотрудника.
   * @param {string}  employeeId  Идентификатор сотрудника.
   * @param {string}  assistantId Идентификатор ассистента.
   * @returns {Promise<Either>}
   */
  this.setEmployeeAssistant = async (
    employeeId,
    assistantId,
  ) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/assistant`,
        {
          assistant_id: assistantId,
        },
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить рабочий день.
   * @param {string} date       Дата.
   * @param {string} startsTime Время начала.
   * @param {string} endsTime   Время окончания.
   * @returns {Promise<Either>}
   */
  this.setWorkDay = async (date, startsTime, endsTime) => {
    const params = { ends_time: endsTime, starts_time: startsTime };
    try {
      const response = await axios.put(
        `/api/schedule-days/${date}/work`,
        params,
      );
      return Success(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Установить режим работы на месяц.
   * @param {string}  workMode     Идентификатор режима работы.
   * @param {boolean} onlySelected Только для выбранного месяца.
   * @param {string}  yearAndMonth Год и месяц.
   * @returns {Promise<Either>}
   */
  this.setWorkMode = async (workMode, onlySelected, yearAndMonth) => {
    const params = {
      only_selected: onlySelected,
    };
    try {
      const response = await axios
        .put(`/api/schedule-months/${yearAndMonth}/work-mode/${workMode}`, params);
      return Either.Right(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403].includes(error.response.status)) {
        return Either.Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить список ассистентов.
   * @returns {Promise<any>}
   */
  this.viewAssistantList = async () => {
    try {
      const response = await axios
        .get('/api/employees/assistants');
      return Either.Right(response.data);
    } catch (error) {
      if (error.isAxiosError && [400, 403].includes(error.response.status)) {
        return Either.Left(error.response.data);
      }
      throw error;
    }
  };
}
